import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Icon,
  TextInput,
  DropdownMenuCheckboxItem,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { useAudiencesInfiniteQuery } from '../../../../common/queries/use-audiences-query';
import './audience-picker.scss';
import { Audience } from '../../../../models/audiences/types';

const AUDIENCE_ERROR_ID = 'ugc-audience-error';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

const AudiencePicker = () => {
  const { t } = useTranslation();
  const {
    userGeneratedContent,
    updateUserGeneratedContentFormState,
    memberValidationErrors,
  } = useUserGeneratedContent();
  const showError = memberValidationErrors.audience;
  const [dropdownSelection, setDropdownSelection] = useState<Audience[]>(
    userGeneratedContent.audiences ?? []
  );
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedAudiences = userGeneratedContent.audiences ?? [];
  const buttonLabel =
    selectedAudiences
      .map((a) => a.title)
      .sort()
      .join(', ') || t('user_generated_content.select_audiences');

  const selectedNames = dropdownSelection.map((a) => a.name);

  // Sync local dropdown selection when post data loads (e.g. editing a saved post)
  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownSelection(userGeneratedContent.audiences ?? []);
    }
  }, [userGeneratedContent.audiences, isDropdownOpen]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setInputValue('');
      setDebouncedSearch('');
    }
  }, [isDropdownOpen]);

  // Debounce the search input before firing a remote query
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setDebouncedSearch(inputValue.trim()),
      DEBOUNCE_MS
    );
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useAudiencesInfiniteQuery({
      q: debouncedSearch || undefined,
      page_size: PAGE_SIZE,
    });

  const optionsRef = useRef<HTMLDivElement>(null);
  // Track the sentinel via a callback ref (node in state), not a plain ref.
  // The list renders inside a Radix portal that mounts a tick after the menu
  // opens, so an effect keyed on `isDropdownOpen` runs while the sentinel is
  // still null, bails, and never re-arms — pagination then never fires. Keying
  // the observer on the sentinel node guarantees it attaches once the element
  // is actually in the DOM (and re-attaches if it remounts).
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelEl || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      // rootMargin pre-fetches the next page before the sentinel scrolls fully
      // into view. Mirrors the chat-history panel infinite scroll.
      { root: optionsRef.current, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const audiences = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map((resource) => resource.attributes)
      ) ?? [],
    [data]
  );

  // Pin the committed selection to the top so members see their picks without
  // scrolling. The selection holds full Audience objects, so a chosen audience
  // shows even when its page has not been fetched yet (the list is remote
  // paginated). Skip pinning while searching so a non-matching selection does
  // not force itself into the filtered results.
  const displayAudiences = useMemo(() => {
    if (debouncedSearch) return audiences;
    const selected = userGeneratedContent.audiences ?? [];
    const pinnedNames = new Set(selected.map((a) => a.name));
    return [...selected, ...audiences.filter((a) => !pinnedNames.has(a.name))];
  }, [debouncedSearch, userGeneratedContent.audiences, audiences]);

  const handleCheckedChange = useCallback(
    (audience: Audience, checked: boolean) => {
      const updated = checked
        ? [...dropdownSelection, audience]
        : dropdownSelection.filter((a) => a.name !== audience.name);
      setDropdownSelection(updated);
    },
    [dropdownSelection]
  );

  const applyChanges = useCallback(() => {
    updateUserGeneratedContentFormState({ audiences: dropdownSelection });
    setIsDropdownOpen(false);
  }, [dropdownSelection, updateUserGeneratedContentFormState]);

  return (
    <div className="ugc-audience-picker-card">
      <div className="ugc-audience-picker-card__label text-body">
        {t('user_generated_content.audiences')}
      </div>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            label={buttonLabel}
            rightIcon={<Icon>add</Icon>}
            size="compact"
            variant="outline"
            className={classNames(
              'ugc-audience-picker-card__trigger text-body',
              showError && 'ugc-audience-picker-card__trigger--error'
            )}
            aria-invalid={showError || undefined}
            aria-describedby={showError ? AUDIENCE_ERROR_ID : undefined}
            aria-errormessage={showError ? AUDIENCE_ERROR_ID : undefined}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="ugc-audience-picker-card__content"
        >
          <div className="ugc-audience-picker-card__search">
            <div className="ugc-audience-picker-card__cta text-subheading-bold">
              {t('user_generated_content.select_audiences')}
            </div>
            {/* stopPropagation prevents Radix DropdownMenu typeahead from stealing focus */}
            <div role="none" onKeyDown={(e) => e.stopPropagation()}>
              <TextInput
                className="ugc-audience-picker-card__search_input"
                fullWidth
                value={inputValue}
                onChange={setInputValue}
                rightWidget={<Icon>search</Icon>}
                placeholder={t('user_generated_content.search_audiences')}
              />
            </div>
            <div className="ugc-audience-picker-card__options" ref={optionsRef}>
              {displayAudiences.map((audience) => (
                <DropdownMenuCheckboxItem
                  key={audience.name}
                  label={audience.title}
                  checked={selectedNames.includes(audience.name)}
                  onCheckedChange={(checked: boolean) =>
                    handleCheckedChange(audience, checked)
                  }
                  onSelect={(e: Event) => e.preventDefault()}
                  className={'ugc-audience-picker-card__option'}
                />
              ))}
              {isLoading && (
                <div className="ugc-audience-picker-card__no_audiences text-body">
                  {t('common.loading')}
                </div>
              )}
              {!isLoading && displayAudiences.length === 0 && (
                <div className="ugc-audience-picker-card__no_audiences text-body">
                  {t('user_generated_content.no_audiences_found')}
                </div>
              )}
              {hasNextPage && (
                <div
                  ref={setSentinelEl}
                  className="ugc-audience-picker-card__load_more"
                />
              )}
              {isFetchingNextPage && (
                <div className="ugc-audience-picker-card__no_audiences text-body">
                  {t('common.loading')}
                </div>
              )}
            </div>
          </div>
          <div className="ugc-audience-picker-card__actions">
            <Button
              label={t('user_generated_content.clear_audience_selection')}
              variant="text"
              onClick={() => setDropdownSelection([])}
            />
            <Button
              label={t('user_generated_content.apply_audience_selection')}
              variant="primary"
              onClick={applyChanges}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {showError && (
        <div
          id={AUDIENCE_ERROR_ID}
          role="alert"
          className="ugc-audience-picker-card__error"
        >
          <Icon size={16}>warning</Icon>
          <span className="text-caption">
            {t('user_generated_content.validation.topic_or_audience_required')}
          </span>
        </div>
      )}
    </div>
  );
};

export default AudiencePicker;
